"use client";

import { useMemo, useState } from "react";
import type { MiroFishGraphEdge, MiroFishGraphPayload } from "@/types/mirofish";
import { layoutMiroFishGraph } from "@/lib/mirofish-graph-layout";

const GRAPH_WIDTH = 900;
const GRAPH_HEIGHT = 620;

const computeDegrees = (edges: MiroFishGraphEdge[]) => {
  const degree = new Map<string, number>();
  for (const edge of edges) {
    degree.set(edge.source, (degree.get(edge.source) || 0) + 1);
    degree.set(edge.target, (degree.get(edge.target) || 0) + 1);
  }
  return degree;
};

const selectSparseEdges = (edges: MiroFishGraphEdge[], maxEdges: number) => {
  if (edges.length <= maxEdges) {
    return edges;
  }

  const degree = computeDegrees(edges);
  const scored = edges.map((edge) => ({
    edge,
    score:
      (degree.get(edge.source) || 0) +
      (degree.get(edge.target) || 0) +
      (edge.name?.length || 0) * 0.01,
  }));

  scored.sort((left, right) => right.score - left.score);
  return scored.slice(0, maxEdges).map((item) => item.edge);
};

export default function MiroFishGraphPanel({
  graph,
  loading,
  headline,
}: {
  graph?: MiroFishGraphPayload | null;
  loading?: boolean;
  headline?: string;
}) {
  const [selectedNodeId, setSelectedNodeId] = useState<string | null>(null);
  const [zoom, setZoom] = useState(1);
  const [simplifyGraph, setSimplifyGraph] = useState(false);
  const [showEdgeLabels, setShowEdgeLabels] = useState(true);
  const [layoutNonce, setLayoutNonce] = useState(0);
  const [selectedType, setSelectedType] = useState<string>("ALL");
  const [pan, setPan] = useState({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });

  const relationTypes = useMemo(() => {
    if (!graph) {
      return [];
    }
    const unique = new Set(graph.edges.map((edge) => edge.factType || edge.name || "RELATES_TO"));
    return Array.from(unique).slice(0, 8);
  }, [graph]);

  const visibleEdges = useMemo(() => {
    if (!graph) {
      return [];
    }
    const byType =
      selectedType === "ALL"
        ? graph.edges
        : graph.edges.filter(
            (edge) => (edge.factType || edge.name || "RELATES_TO") === selectedType
          );
    return simplifyGraph ? selectSparseEdges(byType, 220) : byType;
  }, [graph, simplifyGraph, selectedType]);

  const neighborIds = useMemo(() => {
    if (!graph || !selectedNodeId) {
      return null;
    }
    const neighbors = new Set<string>();
    for (const edge of visibleEdges) {
      if (edge.source === selectedNodeId) {
        neighbors.add(edge.target);
      }
      if (edge.target === selectedNodeId) {
        neighbors.add(edge.source);
      }
    }
    neighbors.add(selectedNodeId);
    return neighbors;
  }, [graph, selectedNodeId, visibleEdges]);

  const positions = useMemo(() => {
    if (!graph) {
      return new Map<string, { x: number; y: number }>();
    }
    return layoutMiroFishGraph({
      nodes: graph.nodes,
      edges: visibleEdges,
      width: GRAPH_WIDTH,
      height: GRAPH_HEIGHT,
      spread: 1.2,
      jitter: 0.08 + (layoutNonce % 7) * 0.01,
    });
  }, [graph, visibleEdges, layoutNonce]);

  const nodeMap = useMemo(() => {
    if (!graph) {
      return new Map<string, MiroFishGraphPayload["nodes"][number]>();
    }
    return new Map(graph.nodes.map((node) => [node.id, node]));
  }, [graph]);

  const selectedNode = selectedNodeId ? nodeMap.get(selectedNodeId) ?? null : null;

  const showAllLabels = zoom >= 0.95;

  if (loading) {
    return (
      <div className="flex h-full min-h-[520px] items-center justify-center rounded-[32px] border border-white/10 bg-white/5 text-white/60">
        Loading graph memory...
      </div>
    );
  }

  if (!graph) {
    return (
      <div className="flex h-full min-h-[520px] items-center justify-center rounded-[32px] border border-white/10 bg-white/5 text-white/55">
        Waiting for ontology and graph generation.
      </div>
    );
  }

  return (
    <div className="relative flex h-full min-h-[520px] flex-col overflow-hidden rounded-[32px] border border-white/10 bg-[radial-gradient(circle_at_top,rgba(56,189,248,0.10),transparent_34%),linear-gradient(180deg,rgba(8,9,15,0.96),rgba(6,6,10,0.98))] text-white">
      <div className="flex flex-wrap items-center justify-between gap-3 border-b border-white/10 px-4 py-3">
        <div>
          <div className="text-[10px] uppercase tracking-[0.24em] text-white/40">Graph Panel</div>
          <div className="mt-1 text-lg font-medium text-white">
            {headline || "Graph relationship visualization"}
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-3 text-xs text-white/65">
          <button
            type="button"
            className="rounded border border-white/20 px-2 py-1 text-[11px] hover:bg-white/10"
            onClick={() => setLayoutNonce((value) => value + 1)}
          >
            Refresh
          </button>
          <label className="flex cursor-pointer items-center gap-2">
            <input
              type="checkbox"
              className="accent-white"
              checked={simplifyGraph}
              onChange={(event) => setSimplifyGraph(event.target.checked)}
            />
            Simplify (top links)
          </label>
          <label className="flex cursor-pointer items-center gap-2">
            <input
              type="checkbox"
              className="accent-white"
              checked={showEdgeLabels}
              onChange={(event) => setShowEdgeLabels(event.target.checked)}
            />
            Edge labels
          </label>
          <div className="rounded border border-white/20 bg-white/[0.06] px-2 py-1 text-[11px] text-white/70">
            Zoom {zoom.toFixed(2)}x — scroll wheel
          </div>
          <div className="text-right text-[11px] text-white/55">
            <div>{graph.nodeCount} nodes</div>
            <div>
              {visibleEdges.length}/{graph.edgeCount} edges shown
            </div>
          </div>
        </div>
      </div>

      <div className="relative min-h-0 flex-1">
        <svg
          viewBox={`0 0 ${GRAPH_WIDTH} ${GRAPH_HEIGHT}`}
          className={`h-full min-h-[460px] w-full touch-none ${isDragging ? "cursor-grabbing" : "cursor-grab"}`}
          onWheel={(event) => {
            event.preventDefault();
            event.stopPropagation();
            const factor = event.deltaY > 0 ? 0.92 : 1.09;
            setZoom((current) => Math.min(2.6, Math.max(0.55, current * factor)));
          }}
          onMouseDown={(event) => {
            if (event.button !== 0) {
              return;
            }
            setIsDragging(true);
            setDragStart({ x: event.clientX, y: event.clientY });
          }}
          onMouseMove={(event) => {
            if (!isDragging) {
              return;
            }
            const dx = event.clientX - dragStart.x;
            const dy = event.clientY - dragStart.y;
            setPan((current) => ({ x: current.x + dx, y: current.y + dy }));
            setDragStart({ x: event.clientX, y: event.clientY });
          }}
          onMouseUp={() => setIsDragging(false)}
          onMouseLeave={() => setIsDragging(false)}
        >
          <defs>
            <pattern id="mirofish-dot-grid" width="20" height="20" patternUnits="userSpaceOnUse">
              <circle cx="1" cy="1" r="1" fill="rgba(255,255,255,0.08)" />
            </pattern>
            <radialGradient id="mirofish-node-glow">
              <stop offset="0%" stopColor="rgba(56,189,248,0.22)" />
              <stop offset="100%" stopColor="rgba(56,189,248,0)" />
            </radialGradient>
          </defs>

          <rect x="0" y="0" width={GRAPH_WIDTH} height={GRAPH_HEIGHT} fill="url(#mirofish-dot-grid)" />

          <g
            transform={`translate(${pan.x},${pan.y}) translate(${GRAPH_WIDTH / 2},${GRAPH_HEIGHT / 2}) scale(${zoom}) translate(${-GRAPH_WIDTH / 2},${-GRAPH_HEIGHT / 2})`}
          >
            {visibleEdges.map((edge) => {
              const source = positions.get(edge.source);
              const target = positions.get(edge.target);

              if (!source || !target) {
                return null;
              }

              const midX = (source.x + target.x) / 2;
              const midY = (source.y + target.y) / 2;

              return (
                <g key={edge.id}>
                  <line
                    x1={source.x}
                    y1={source.y}
                    x2={target.x}
                    y2={target.y}
                    stroke="rgba(255,255,255,0.20)"
                    strokeWidth="1"
                  />
                  {showEdgeLabels && showAllLabels ? (
                    <text
                      x={midX}
                      y={midY}
                      fill="rgba(255,255,255,0.52)"
                      fontSize="8.5"
                      textAnchor="middle"
                    >
                      {(edge.name || edge.factType || "rel").slice(0, 24)}
                    </text>
                  ) : null}
                </g>
              );
            })}

            {graph.nodes.map((node) => {
              const position = positions.get(node.id);
              if (!position) {
                return null;
              }

              const selected = node.id === selectedNodeId;
              const highlight =
                !neighborIds || neighborIds.has(node.id) ? 1 : 0.22;
              const labelVisible =
                showAllLabels || selected || (neighborIds && neighborIds.has(node.id));

              return (
                <g
                  key={node.id}
                  onClick={() => setSelectedNodeId(node.id)}
                  className="cursor-pointer"
                  opacity={highlight}
                >
                  <circle
                    cx={position.x}
                    cy={position.y}
                    r={selected ? 9 : 6}
                    fill={node.color}
                    fillOpacity={selected ? 0.95 : 0.88}
                  />
                  <circle
                    cx={position.x}
                    cy={position.y}
                    r={selected ? 16 : 12}
                    fill="url(#mirofish-node-glow)"
                  />
                  {labelVisible ? (
                    <text
                      x={position.x + 7}
                      y={position.y - 7}
                      textAnchor="start"
                      fill="rgba(255,255,255,0.78)"
                      fontSize="8.8"
                      fontWeight="500"
                    >
                      {node.name.slice(0, 12)}
                    </text>
                  ) : null}
                </g>
              );
            })}
          </g>
        </svg>

        <div className="absolute right-4 top-4 flex max-w-[52%] flex-wrap gap-2">
          <button
            type="button"
            className={`rounded border px-2 py-1 text-[10px] ${
              selectedType === "ALL"
                ? "border-white bg-white text-black"
                : "border-white/20 bg-black/60 text-white/75 hover:bg-white/10"
            }`}
            onClick={() => setSelectedType("ALL")}
          >
            ALL
          </button>
          {relationTypes.map((type) => (
            <button
              key={type}
              type="button"
              className={`rounded border px-2 py-1 text-[10px] ${
                selectedType === type
                  ? "border-white bg-white text-black"
                  : "border-white/20 bg-black/60 text-white/75 hover:bg-white/10"
              }`}
              onClick={() => setSelectedType(type)}
              title={type}
            >
              {type.slice(0, 18)}
            </button>
          ))}
        </div>

        {selectedNode ? (
          <div className="absolute bottom-4 left-4 right-4 rounded-[14px] border border-white/15 bg-black/70 p-4 backdrop-blur">
            <div className="flex items-center justify-between gap-3">
              <div>
                <div className="text-xs uppercase tracking-[0.24em] text-white/45">
                  {selectedNode.entityType}
                </div>
                <div className="mt-1 text-lg font-semibold text-white">{selectedNode.name}</div>
              </div>
              <button
                type="button"
                className="text-sm text-white/50 transition hover:text-white"
                onClick={() => setSelectedNodeId(null)}
              >
                Close
              </button>
            </div>
            <p className="mt-2 max-w-3xl text-sm leading-6 text-white/70">{selectedNode.summary}</p>
          </div>
        ) : null}
      </div>
    </div>
  );
}
